// REACT IMPORTS
import React, { useContext, useEffect, useMemo, useState } from "react";

// CSS
import "./AutomationLogsDashboard.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import {
	Chip,
	CircularProgress,
	Collapse,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
} from "@mui/material";

// MUI ICONS
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ViewListIcon from "@mui/icons-material/ViewList";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import AutomationLog from "../AutomationLog/AutomationLog";

// CONSTANTS
// Health colors match the ProjectHealthDashboard convention (its sibling health page).
// "dormant" = no runs in the active window at all - distinct from "healthy" (recently ran, all OK)
const STATUS_COLORS = {
	failing: "#c62828",
	flaky: "#ca8a04",
	healthy: "#2e7d32",
	dormant: "#757575",
};
const STATUS_BG_COLORS = {
	failing: "#ffebee",
	flaky: "#fffde7",
	healthy: "#e8f5e9",
	dormant: "#f5f5f5",
};
const STATUS_LABELS = {
	failing: "Failing",
	flaky: "Flaky",
	healthy: "Healthy",
	dormant: "Dormant",
};
const STATUS_ICONS = {
	failing: ErrorIcon,
	flaky: WarningIcon,
	healthy: CheckCircleIcon,
	dormant: PauseCircleOutlineIcon,
};
const STATUS_ORDER = ["failing", "flaky", "healthy", "dormant"];
const WINDOW_OPTIONS = [
	{ value: 1, label: "24h" },
	{ value: 7, label: "7d" },
	{ value: 30, label: "30d" },
];
const TREND_RUN_COUNT = 20;
const RECENT_RUNS_SHOWN = 10;

// HELPERS
const formatRelativeTime = (ts, now) => {
	const minutes = Math.max(0, Math.round((now - ts) / 60000));
	if (minutes < 1) return "just now";
	if (minutes < 60) return `${minutes} min ago`;
	const hours = Math.round(minutes / 60);
	if (hours < 24) return `${hours} h ago`;
	return `${Math.round(hours / 24)} d ago`;
};

const formatAbsoluteTime = (ts) =>
	new Date(ts).toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});

const toRun = (row) => ({
	ts: new Date(row.created).getTime(),
	ok: Number(row.success) === 1,
	error: row.message || "",
});

// Display label only - NULL/""/whitespace-only tool_name values all read as
// "Unknown" to a person, but are distinct values in SQL (GROUP BY/PARTITION BY
// treat them as separate groups). Never use this for identity/keys.
const toToolLabel = (row) => (row.tool_name || "").trim() || "Unknown";

// Stable identity, distinct for every raw tool_name value including
// NULL/""/whitespace variants that all share the "Unknown" label above.
// React keys and cross-query merging must use this, not the label - reusing
// the label would collapse multiple real tools into one React key, which
// breaks reconciliation (rows visibly scramble on re-sort/re-render).
const toToolKey = (row) => JSON.stringify(row.tool_name ?? null);

// Ingest raw automation_log rows -> per-tool run history (newest run first),
// keyed by identity (not display label) to avoid collapsing distinct tools.
const groupRunsByTool = (rows) => {
	const byTool = {};
	(rows || []).forEach((row) => {
		const key = toToolKey(row);
		const entry =
			byTool[key] || (byTool[key] = { key, name: toToolLabel(row), runs: [] });
		entry.runs.push(toRun(row));
	});
	Object.values(byTool).forEach((entry) =>
		entry.runs.sort((a, b) => b.ts - a.ts)
	);
	return byTool;
};

// Merges "every tool that has ever run" (latestPerTool) with "tools with
// activity in the active window" (recentRuns) so a long-silent tool still
// shows up - with its real last-run age - instead of disappearing outright.
const buildToolList = (latestPerTool, recentRunRows) => {
	const recentByTool = groupRunsByTool(recentRunRows);

	return (latestPerTool || []).map((row) => {
		const key = toToolKey(row);
		const name = toToolLabel(row);
		const recentEntry = recentByTool[key];
		const hasRecentActivity = Boolean(recentEntry);
		return {
			key,
			name,
			hasRecentActivity,
			runs: hasRecentActivity ? recentEntry.runs : [toRun(row)],
		};
	});
};

// Derives a tool's health for the selected time window. "Dormant" means no
// runs fall within the CURRENTLY SELECTED window (24h/7d/30d) - not just
// "outside the backend's 180-day fetch" - so a tool whose last run was, say,
// 169 days ago (inside the 180-day fetch, outside a 7d window) is correctly
// dormant rather than being graded on that one stale run's outcome.
const deriveToolHealth = (tool, windowDays, now) => {
	const windowMs = windowDays * 86400000;
	const inWindow = tool.runs.filter((r) => r.ts >= now - windowMs);
	const latest = tool.runs[0];
	const lastFail = tool.runs.find((r) => !r.ok);

	if (inWindow.length === 0) {
		return { tool, status: "dormant", rate: null, latest, lastFail };
	}

	const okCount = inWindow.filter((r) => r.ok).length;
	const rate = Math.round((100 * okCount) / inWindow.length);
	const anyFail = inWindow.some((r) => !r.ok);
	const status = !latest.ok ? "failing" : anyFail ? "flaky" : "healthy";
	return { tool, status, rate, latest, lastFail };
};

// REACT COMPONENT
const AutomationLogsDashboard = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// DATA STATE
	const [latestPerTool, setLatestPerTool] = useState([]);
	const [recentRuns, setRecentRuns] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [lastUpdated, setLastUpdated] = useState(null);

	// CONTROL STATE
	const [view, setView] = useState("dashboard");
	// Bumped by the header's reload button so the embedded full list (which
	// fetches its own data) refetches too, regardless of which view is active.
	const [listRefreshTrigger, setListRefreshTrigger] = useState(0);
	const [query, setQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const [windowDays, setWindowDays] = useState(7);
	const [sortKey, setSortKey] = useState("attention");
	const [sortDir, setSortDir] = useState(1);
	const [expandedTool, setExpandedTool] = useState(null);

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Automation Logs Dashboard",
			employeeID: employeeID,
			notes: "Accessed the Automation Logs Dashboard page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		fetchDashboard();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// API HELPER FUNCTIONS
	const fetchDashboard = async () => {
		setIsLoading(true);

		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/logs/automation/dashboard"
			);
			setLatestPerTool(response.data.latestPerTool);
			setRecentRuns(response.data.recentRuns);
			setLastUpdated(Date.now());
		} catch (error) {
			console.error(error);
		}

		setIsLoading(false);
	};

	// DERIVED DATA
	const now = lastUpdated || Date.now();

	const tools = useMemo(
		() => buildToolList(latestPerTool, recentRuns),
		[latestPerTool, recentRuns]
	);

	const derivedTools = useMemo(
		() => tools.map((tool) => deriveToolHealth(tool, windowDays, now)),
		[tools, windowDays, now]
	);

	const counts = useMemo(
		() =>
			derivedTools.reduce(
				(acc, d) => {
					acc[d.status] += 1;
					return acc;
				},
				{ failing: 0, flaky: 0, healthy: 0, dormant: 0 }
			),
		[derivedTools]
	);

	const filteredSorted = useMemo(() => {
		const q = query.trim().toLowerCase();

		const list = derivedTools.filter((d) => {
			if (statusFilter !== "all" && d.status !== statusFilter) {
				return false;
			}
			if (q && !d.tool.name.toLowerCase().includes(q)) return false;
			return true;
		});

		// Every comparator ends in a key tie-break (never just name) so multiple
		// tools that legitimately share a display label (e.g. several distinct
		// blank tool_name values that all read as "Unknown") still sort
		// deterministically instead of swapping order on every re-render.
		const rank = { failing: 0, flaky: 1, healthy: 2, dormant: 3 };
		const byNameThenKey = (a, b) =>
			a.tool.name.localeCompare(b.tool.name) ||
			a.tool.key.localeCompare(b.tool.key);
		const comparators = {
			attention: (a, b) =>
				rank[a.status] - rank[b.status] || byNameThenKey(a, b),
			name: byNameThenKey,
			last: (a, b) => b.latest.ts - a.latest.ts || byNameThenKey(a, b),
			success: (a, b) =>
				(a.rate ?? -1) - (b.rate ?? -1) || byNameThenKey(a, b),
		};
		const cmp = comparators[sortKey] || comparators.attention;

		return [...list].sort((a, b) => cmp(a, b) * sortDir);
	}, [derivedTools, query, statusFilter, sortKey, sortDir]);

	// INPUT HANDLERS
	const handleSort = (key) => {
		setSortDir((prevDir) => (sortKey === key ? -prevDir : 1));
		setSortKey(key);
	};

	const handleToggleStatusFilter = (status) => {
		setStatusFilter((prev) => (prev === status ? "all" : status));
	};

	const handleToggleExpand = (key) => {
		setExpandedTool((prev) => (prev === key ? null : key));
	};

	const handleRefresh = () => {
		fetchDashboard();
		setListRefreshTrigger((prev) => prev + 1);
	};

	// RENDER
	return (
		<div className="AutomationLogsDashboard">
			<div className="automationLogsDashboardHeader">
				<div className="automationLogsDashboardHeaderRow">
					<div className="automationLogsDashboardHeaderTitle">
						Automation Logs
					</div>
					<div className="automationLogsDashboardHeaderActions">
						{lastUpdated && (
							<span className="automationLogsDashboardUpdatedText">
								Updated {formatAbsoluteTime(lastUpdated)}
							</span>
						)}
						<Tooltip title="Refresh">
							<span>
								<IconButton
									onClick={handleRefresh}
									disabled={isLoading}
									size="small"
								>
									<RefreshIcon />
								</IconButton>
							</span>
						</Tooltip>
						<ToggleButtonGroup
							size="small"
							value={view}
							exclusive
							onChange={(e, value) => value && setView(value)}
							sx={{
								height: 36,
								"& .MuiToggleButton-root": {
									height: 36,
									boxSizing: "border-box",
									textTransform: "none",
									color: "text.secondary",
									borderColor: "#e0e0e0",
								},
								"& .MuiToggleButton-root.Mui-selected": {
									backgroundColor: "primary.main",
									color: "#fff",
								},
								"& .MuiToggleButton-root.Mui-selected:hover": {
									backgroundColor: "primary.main",
								},
							}}
						>
							<ToggleButton value="dashboard">
								<DashboardIcon
									fontSize="small"
									style={{ marginRight: 6 }}
								/>
								Dashboard
							</ToggleButton>
							<ToggleButton value="list">
								<ViewListIcon
									fontSize="small"
									style={{ marginRight: 6 }}
								/>
								Full List
							</ToggleButton>
						</ToggleButtonGroup>
					</div>
				</div>
			</div>

			{view === "list" && (
				<AutomationLog
					embedded
					refreshTrigger={listRefreshTrigger}
				/>
			)}

			{view === "dashboard" && isLoading && tools.length === 0 && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}

			{view === "dashboard" && !(isLoading && tools.length === 0) && (
				<div className="automationLogsDashboardBody">
					{/* Summary strip */}
					<div className="automationLogsDashboardSummaryStrip">
						{STATUS_ORDER.map((status) => {
							const StatusIcon = STATUS_ICONS[status];
							const isMuted =
								statusFilter !== "all" &&
								statusFilter !== status;
							return (
								<button
									key={status}
									type="button"
									className="automationLogsDashboardSummaryCard"
									onClick={() =>
										handleToggleStatusFilter(status)
									}
									style={{
										borderColor: STATUS_COLORS[status],
										background: isMuted
											? "#fff"
											: STATUS_BG_COLORS[status],
									}}
								>
									<StatusIcon
										style={{ color: STATUS_COLORS[status] }}
										fontSize="small"
									/>
									<span
										className="automationLogsDashboardSummaryCount"
										style={{ color: STATUS_COLORS[status] }}
									>
										{counts[status]}
									</span>
									<span
										className="automationLogsDashboardSummaryLabel"
										style={{ color: STATUS_COLORS[status] }}
									>
										{STATUS_LABELS[status]}
									</span>
								</button>
							);
						})}
					</div>

					{/* Toolbar */}
					<div className="automationLogsDashboardToolbar">
						<TextField
							size="small"
							placeholder="Search tools..."
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							sx={{ width: 240 }}
						/>
						<ToggleButtonGroup
							size="small"
							value={statusFilter}
							exclusive
							onChange={(e, value) =>
								value && setStatusFilter(value)
							}
						>
							<ToggleButton value="all">All</ToggleButton>
							<ToggleButton value="failing">Failing</ToggleButton>
							<ToggleButton value="flaky">Flaky</ToggleButton>
							<ToggleButton value="healthy">Healthy</ToggleButton>
							<ToggleButton value="dormant">Dormant</ToggleButton>
						</ToggleButtonGroup>
						<div className="automationLogsDashboardToolbarSpacer" />
						<ToggleButtonGroup
							size="small"
							value={windowDays}
							exclusive
							onChange={(e, value) => value && setWindowDays(value)}
						>
							{WINDOW_OPTIONS.map((opt) => (
								<ToggleButton key={opt.value} value={opt.value}>
									{opt.label}
								</ToggleButton>
							))}
						</ToggleButtonGroup>
					</div>

					{/* Table */}
					<TableContainer className="automationLogsDashboardTableContainer">
						<Table stickyHeader size="small">
							<TableHead>
								<TableRow>
									<TableCell>Status</TableCell>
									<TableCell>
										<TableSortLabel
											active={sortKey === "name"}
											direction={
												sortDir === 1 ? "asc" : "desc"
											}
											onClick={() => handleSort("name")}
										>
											Tool
										</TableSortLabel>
									</TableCell>
									<TableCell>
										<TableSortLabel
											active={sortKey === "last"}
											direction={
												sortDir === 1 ? "asc" : "desc"
											}
											onClick={() => handleSort("last")}
										>
											Last Run
										</TableSortLabel>
									</TableCell>
									<TableCell>
										Trend &middot; Last {TREND_RUN_COUNT}
									</TableCell>
									<TableCell align="right">
										<TableSortLabel
											active={sortKey === "success"}
											direction={
												sortDir === 1 ? "asc" : "desc"
											}
											onClick={() =>
												handleSort("success")
											}
										>
											Success
										</TableSortLabel>
									</TableCell>
									<TableCell>Last Error</TableCell>
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredSorted.length === 0 && (
									<TableRow>
										<TableCell colSpan={7}>
											<J2NoResultsFound />
										</TableCell>
									</TableRow>
								)}
								{filteredSorted.map((d) => {
									const bars = d.tool.runs
										.slice(0, TREND_RUN_COUNT)
										.slice()
										.reverse();
									const expanded =
										expandedTool === d.tool.key;

									return (
										<React.Fragment key={d.tool.key}>
											<TableRow
												hover
												onClick={() =>
													handleToggleExpand(
														d.tool.key
													)
												}
												className="automationLogsDashboardRow"
												style={{
													background:
														d.status === "failing" ||
														d.status === "dormant"
															? STATUS_BG_COLORS[
																	d.status
															  ]
															: undefined,
												}}
											>
												<TableCell>
													<span className="automationLogsDashboardStatusCell">
														<span
															className="automationLogsDashboardStatusDot"
															style={{
																background:
																	STATUS_COLORS[
																		d
																			.status
																	],
															}}
														/>
														<span
															style={{
																color: STATUS_COLORS[
																	d.status
																],
																fontWeight: 500,
																fontSize: 12,
															}}
														>
															{
																STATUS_LABELS[
																	d.status
																]
															}
														</span>
													</span>
												</TableCell>
												<TableCell>
													{d.tool.name}
												</TableCell>
												<TableCell>
													{formatRelativeTime(
														d.latest.ts,
														now
													)}
												</TableCell>
												<TableCell>
													<div className="automationLogsDashboardTrend">
														{bars.map(
															(run, i) => (
																<Tooltip
																	key={i}
																	title={
																		(run.ok
																			? "OK · "
																			: "Failed · ") +
																		formatAbsoluteTime(
																			run.ts
																		)
																	}
																>
																	<span
																		className="automationLogsDashboardTrendBar"
																		style={{
																			background:
																				run.ok
																					? "#66BB6A"
																					: "#F44336",
																		}}
																	/>
																</Tooltip>
															)
														)}
													</div>
												</TableCell>
												<TableCell
													align="right"
													style={{
														color: STATUS_COLORS[
															d.status
														],
														fontWeight: 500,
													}}
												>
													{d.rate === null
														? "—"
														: `${d.rate}%`}
												</TableCell>
												<TableCell className="automationLogsDashboardLastError">
													{d.lastFail
														? d.lastFail.error.split(
																"\n"
														  )[0]
														: "—"}
												</TableCell>
												<TableCell>
													<IconButton size="small">
														{expanded ? (
															<KeyboardArrowUpIcon />
														) : (
															<KeyboardArrowDownIcon />
														)}
													</IconButton>
												</TableCell>
											</TableRow>
											<TableRow>
												<TableCell
													colSpan={7}
													style={{
														paddingTop: 0,
														paddingBottom: 0,
													}}
												>
													<Collapse
														in={expanded}
														timeout="auto"
														unmountOnExit
													>
														<div className="automationLogsDashboardExpandedPanel">
															{d.lastFail && (
																<div className="automationLogsDashboardExpandedSection">
																	<div className="automationLogsDashboardExpandedSectionLabel">
																		Latest
																		Error
																	</div>
																	<pre className="automationLogsDashboardErrorBox">
																		{formatAbsoluteTime(
																			d
																				.lastFail
																				.ts
																		)}
																		{"\n"}
																		{
																			d
																				.lastFail
																				.error
																		}
																	</pre>
																</div>
															)}
															<div className="automationLogsDashboardExpandedSectionLabel">
																Recent Runs
															</div>
															<div className="automationLogsDashboardRecentRuns">
																{d.tool.runs
																	.slice(
																		0,
																		RECENT_RUNS_SHOWN
																	)
																	.map(
																		(
																			run,
																			i
																		) => (
																			<div
																				className="automationLogsDashboardRecentRunRow"
																				key={
																					i
																				}
																			>
																				<span className="automationLogsDashboardRecentRunTs">
																					{formatAbsoluteTime(
																						run.ts
																					)}
																				</span>
																				<Chip
																					size="small"
																					label={
																						run.ok
																							? "OK"
																							: "Failed"
																					}
																					color={
																						run.ok
																							? "success"
																							: "error"
																					}
																					variant="outlined"
																				/>
																				<span className="automationLogsDashboardRecentRunError">
																					{run.ok
																						? "—"
																						: run.error.split(
																								"\n"
																						  )[0]}
																				</span>
																			</div>
																		)
																	)}
															</div>
														</div>
													</Collapse>
												</TableCell>
											</TableRow>
										</React.Fragment>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</div>
			)}
		</div>
	);
};

export default AutomationLogsDashboard;
